import { create } from 'zustand';
import { useNodeStore } from './nodeStore';
import { useGroupStore } from './groupStore';
import { NodeType } from '../types';

interface HandlePosition {
  handleId: string;
  x: number;
  y: number;
}

interface SelectionState {
  selectedNodeIds: Set<string>;
  activeGroupId: string | null;

  selectNode: (id: string, multi?: boolean) => void;
  deselectNode: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  deleteSelected: () => void;
  setSelectedNodeIds: (ids: string[]) => void;
  setActiveGroupId: (groupId: string | null) => void;
  isSelected: (id: string) => boolean;
  getSelectedArray: () => string[];
  getSelectedCount: () => number;

  mouseX: number;
  mouseY: number;
  activeHandleId: string | null;
  handlePositions: Map<string, HandlePosition>;
  isConnecting: boolean;
  isNodeDragging: boolean;
  setNodeDragging: (v: boolean) => void;

  isBoxSelecting: boolean;
  setBoxSelecting: (v: boolean) => void;

  setMousePosition: (x: number, y: number) => void;
  registerHandle: (handleId: string, x: number, y: number) => void;
  unregisterHandle: (handleId: string) => void;
  setActiveHandle: (handleId: string | null) => void;
  setConnecting: (isConnecting: boolean) => void;
  getClosestHandle: (snapRadius: number) => string | null;
  initGlobalListener: () => void;
}

// 连接点活动区域半径：鼠标进入该半径即触发磁吸吸附
const BASE_SNAP_RADIUS = 90;
// 释放半径必须大于磁吸半径，防止在边界抖动
const BASE_RELEASE_RADIUS = 120;

let globalListenerAttached = false;

// 方案 7：magneticZoom 移出 Zustand，避免每次 set 触发 200+ 订阅器通知
// 先例：selectionStore.ts 自身的 globalListenerAttached 就是模块级变量
// 核查确认：没有任何组件响应式订阅 magneticZoom，仅在 setMousePosition 中读取
let magneticZoom = 1;
export function setMagneticZoom(z: number) {
  if (magneticZoom === z) return;
  magneticZoom = z;
}
export function getMagneticZoom(): number {
  return magneticZoom;
}

// 方案 2：registerHandle RAF 批量更新
// 缩放/平移时 N 个 handle 都调用 registerHandle，若每次都 set 会产生 O(N²) 次订阅器通知
// 改为写入 pendingHandleUpdates，在 RAF 内统一 flush，每帧最多 1 次 set
const pendingHandleUpdates = new Map<string, HandlePosition>();
let handleFlushScheduled = false;

function scheduleHandleUpdate(handleId: string, x: number, y: number) {
  const existing = pendingHandleUpdates.get(handleId);
  if (existing && Math.abs(existing.x - x) < 0.5 && Math.abs(existing.y - y) < 0.5) return;
  pendingHandleUpdates.set(handleId, { handleId, x, y });
  if (handleFlushScheduled) return;
  handleFlushScheduled = true;
  requestAnimationFrame(() => {
    handleFlushScheduled = false;
    if (pendingHandleUpdates.size === 0) return;
    const state = useSelectionStore.getState();
    const newPositions = new Map(state.handlePositions);
    let changed = false;
    pendingHandleUpdates.forEach((pos, id) => {
      const old = newPositions.get(id);
      if (!old || Math.abs(old.x - pos.x) >= 0.5 || Math.abs(old.y - pos.y) >= 0.5) {
        newPositions.set(id, pos);
        changed = true;
      }
    });
    pendingHandleUpdates.clear();
    if (changed) useSelectionStore.setState({ handlePositions: newPositions });
  });
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  selectedNodeIds: new Set<string>(),
  activeGroupId: null,

  selectNode: (id, multi = false) => {
    set(state => {
      const newSet = new Set(multi ? state.selectedNodeIds : []);
      if (multi && newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      const node = useNodeStore.getState().getNodeById(id);
      const activeGroupId = node?.type === NodeType.GROUP ? id : null;
      return { selectedNodeIds: newSet, activeGroupId };
    });
    const node = useNodeStore.getState().getNodeById(id);
    const activeGroupId = node?.type === NodeType.GROUP ? id : null;
    useGroupStore.getState().setActiveGroup(activeGroupId);
  },

  deselectNode: (id) => {
    set(state => {
      const newSet = new Set(state.selectedNodeIds);
      newSet.delete(id);
      return { selectedNodeIds: newSet };
    });
  },

  clearSelection: () => {
    set({ selectedNodeIds: new Set<string>(), activeGroupId: null });
    useGroupStore.getState().setActiveGroup(null);
  },

  selectAll: () => {
    const allNodeIds = useNodeStore.getState().nodes.map(n => n.id);
    set({ selectedNodeIds: new Set(allNodeIds) });
  },

  deleteSelected: () => {
    const { selectedNodeIds } = get();
    if (selectedNodeIds.size === 0) return;
    useNodeStore.getState().deleteNodes(Array.from(selectedNodeIds));
    set({ selectedNodeIds: new Set<string>(), activeGroupId: null });
    useGroupStore.getState().setActiveGroup(null);
  },

  setSelectedNodeIds: (ids) => {
    const newSet = new Set(ids);
    const firstId = ids[0];
    const node = firstId ? useNodeStore.getState().getNodeById(firstId) : null;
    const activeGroupId = node?.type === NodeType.GROUP ? firstId : null;
    set({ selectedNodeIds: newSet, activeGroupId });
    useGroupStore.getState().setActiveGroup(activeGroupId);
  },

  setActiveGroupId: (groupId) => {
    set({ activeGroupId: groupId });
  },

  isSelected: (id) => {
    return get().selectedNodeIds.has(id);
  },

  getSelectedArray: () => {
    return Array.from(get().selectedNodeIds);
  },

  getSelectedCount: () => {
    return get().selectedNodeIds.size;
  },

  mouseX: 0,
  mouseY: 0,
  activeHandleId: null,
  handlePositions: new Map(),
  isConnecting: false,
  isNodeDragging: false,
  // 节点拖动期间冻结磁吸系统：拖动中 nodeStore 不提交位置，handlePositions/originalCenterRef
  // 缓存停留在拖动前坐标，磁吸判定会用旧坐标误激活且释放判定失效，导致圆点飞离节点
  setNodeDragging: (v: boolean) => {
    if (get().isNodeDragging === v) return;
    // 拖动开始时解除已激活的磁吸：activeHandleId 置空触发各 handle 重置 snapOffset，圆点立即回位
    if (v) {
      set({ isNodeDragging: true, activeHandleId: null });
    } else {
      set({ isNodeDragging: false });
    }
  },
  isBoxSelecting: false,
  setBoxSelecting: (v: boolean) => set({ isBoxSelecting: v }),

  initGlobalListener: () => {
    if (globalListenerAttached) return;
    globalListenerAttached = true;

    let rafId: number | null = null;
    let pendingX = 0;
    let pendingY = 0;

    const handler = (e: MouseEvent) => {
      pendingX = e.clientX;
      pendingY = e.clientY;
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          get().setMousePosition(pendingX, pendingY);
        });
      }
    };
    window.addEventListener('mousemove', handler);
  },

  setMousePosition: (x: number, y: number) => {
    const state = get();
    set({ mouseX: x, mouseY: y });

    if (state.isConnecting) return;

    // 节点拖动中：handlePositions 缓存过时，跳过磁吸激活/释放判定（拖动结束由 setNodeDragging(false) 恢复）
    if (state.isNodeDragging) return;

    // 方案 7：直接读取模块级变量 magneticZoom，不经过 store
    const snapRadius = BASE_SNAP_RADIUS * magneticZoom;
    const closest = get().getClosestHandle(snapRadius);

    if (state.activeHandleId) {
      const activePos = state.handlePositions.get(state.activeHandleId);
      if (activePos) {
        const dx = x - activePos.x;
        const dy = y - activePos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        const releaseRadius = BASE_RELEASE_RADIUS * magneticZoom;
        if (distance > releaseRadius) {
          set({ activeHandleId: null });
        }
      }
    } else if (closest) {
      // 鼠标在节点内部（非 handle 区域）时不触发新激活
      // 已激活的连接点不受影响，可以跟随鼠标进入节点内部
      // 用 elementFromPoint 检测鼠标下方元素：
      // - 在节点内 + 非 handle → 阻止激活
      // - 在 handle 上 → 允许激活（handle 在节点边缘）
      // - 在节点外 → 允许激活
      const el = document.elementFromPoint(x, y);
      if (el) {
        const nodeEl = el.closest('.react-flow__node');
        const handleEl = el.closest('.react-flow__handle');
        if (nodeEl && !handleEl) return;
      }
      set({ activeHandleId: closest });
    }
  },

  // 方案 2：registerHandle 改为 RAF 批量更新
  // 缩放/平移时 N 个 handle 调用，每帧最多 1 次 set，避免 O(N²) 订阅器通知
  registerHandle: (handleId: string, x: number, y: number) => {
    scheduleHandleUpdate(handleId, x, y);
  },

  unregisterHandle: (handleId: string) => {
    const state = get();
    const newPositions = new Map(state.handlePositions);
    newPositions.delete(handleId);

    if (state.activeHandleId === handleId) {
      set({ handlePositions: newPositions, activeHandleId: null });
    } else {
      set({ handlePositions: newPositions });
    }
  },

  setActiveHandle: (handleId: string | null) => {
    set({ activeHandleId: handleId });
  },

  setConnecting: (isConnecting: boolean) => {
    set({ isConnecting, activeHandleId: isConnecting ? get().activeHandleId : null });
  },

  getClosestHandle: (snapRadius: number) => {
    const state = get();
    const { mouseX, mouseY, handlePositions, isConnecting } = state;

    if (isConnecting) return null;

    let closestId: string | null = null;
    let closestDistance = snapRadius;

    handlePositions.forEach((pos) => {
      const dx = mouseX - pos.x;
      const dy = mouseY - pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestId = pos.handleId;
      }
    });

    return closestId;
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}

export { BASE_SNAP_RADIUS, BASE_RELEASE_RADIUS };
